/**
 * Тооллого — `/stock-counts` модуль.
 *
 * Нээх мөчид тухайн салбарын тоологдох байршил дахь гутлын snapshot
 * үүснэ (`stock_count_expected`). Дараа нь скан хийж, хаах үед дутуу /
 * илүү нь тогтоогдоно.
 *
 * ⚠️ Хаасан тооллого өөрчлөгдөхгүй. Дутууг БӨӨНӨӨР актлах товч ХАССАН
 *    (2026-11-03) — нээсний дараа зарагдсан гутал «дутуу» болж бүгд
 *    актлагдах эрсдэлтэй байв. Оронд нь ADMIN тооллогыг устгана.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  formatDateTime, formatMnt, formatQty, formatSerialDisplay, isValidSerial, normalizeSerialInput,
} from '@boots/domain';
import { api, type Envelope, type PagedEnvelope } from '../lib/api';
import { useAuth } from '../lib/auth';
import type { Store } from '../lib/types';
import {
  Button, Empty, ErrorNote, Field, Input, Modal, OkNote,
  Select, Spinner, Tabs, Textarea,
} from '../components/ui';
import { DataTable, type Column } from '../components/data-table';
import { Meter } from '../components/meter';

interface CountRow {
  id: string;
  code: string;
  status: 'OPEN' | 'CLOSED';
  expectedCount: number;
  scannedCount: number;
  missingCount: number;
  extraCount: number;
  startedAt: string;
  closedAt: string | null;
  notes: string | null;
  store: { id: string; code: string; name: string };
  countedBy: { username: string; fullName: string } | null;
}

interface Progress {
  expected: number; scanned: number; missing: number; extra: number;
  scans: number; status: string; percent: number;
}

interface MissingRow {
  id: string;
  serial: string | null;
  size: number;
  landedCostMnt: string;
  model: { code: string; name: string };
  location?: { name: string } | null;
}

interface ExtraRow {
  rawSerial: string;
  scannedAt: string;
  unknown: boolean;
  item: { id: string; serial: string | null; size: number; model: { name: string } } | null;
}

type Tab = 'all' | 'OPEN' | 'CLOSED';

export function StockCountsPage() {
  const { can, readOnly } = useAuth();
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('all');
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const list = useQuery({
    queryKey: ['stock-counts'],
    queryFn: () => api<PagedEnvelope<CountRow>>('/stock-counts?limit=100'),
  });

  const canManage = can('STORE_MANAGER', 'WAREHOUSE', 'ADMIN') && !readOnly;

  if (list.isPending) return <Spinner />;
  if (list.error) return <ErrorNote>{(list.error as Error).message}</ErrorNote>;

  const rows = list.data.data;
  const filtered = rows.filter((c) => (tab === 'all' ? true : c.status === tab));
  const counts = {
    all: rows.length,
    OPEN: rows.filter((c) => c.status === 'OPEN').length,
    CLOSED: rows.filter((c) => c.status === 'CLOSED').length,
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2.5">
        <Tabs
          value={tab}
          onChange={setTab}
          tabs={[
            { key: 'all', label: 'Бүгд', count: counts.all },
            { key: 'OPEN', label: 'Нээлттэй', count: counts.OPEN },
            { key: 'CLOSED', label: 'Хаасан', count: counts.CLOSED },
          ]}
        />
        {canManage ? (
          <Button onClick={() => setCreating(true)} className="ml-auto">＋ Тооллого нээх</Button>
        ) : null}
      </div>

      <section className="overflow-hidden rounded-table sm:border sm:border-hair sm:bg-panel">
        <DataTable
          rows={filtered}
          columns={countColumns()}
          rowKey={(c) => c.id}
          onRowClick={(c) => setOpenId(c.id)}
          empty={<Empty>Тооллого алга</Empty>}
        />
      </section>

      {creating ? (
        <CreateCount
          onClose={() => setCreating(false)}
          onCreated={async (id) => {
            setCreating(false);
            await qc.invalidateQueries({ queryKey: ['stock-counts'] });
            setOpenId(id);
          }}
        />
      ) : null}

      {openId ? <CountDetail id={openId} onClose={() => setOpenId(null)} /> : null}
    </div>
  );
}

function CreateCount({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [storeId, setStoreId] = useState('');
  const [notes, setNotes] = useState('');

  const stores = useQuery({
    queryKey: ['ref-stores'],
    queryFn: () => api<Envelope<Store[]>>('/ref/stores'),
  });

  const create = useMutation({
    mutationFn: () =>
      api<Envelope<{ id: string }>>('/stock-counts', {
        method: 'POST',
        body: { storeId, ...(notes.trim() ? { notes: notes.trim() } : {}) },
      }),
    onSuccess: (res) => onCreated(res.data.id),
  });

  return (
    <Modal
      title="Тооллого нээх"
      subtitle="Нээх мөчид байвал зохих үлдэгдлийн snapshot хөлдөнө"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button onClick={() => create.mutate()} disabled={!storeId || create.isPending}>
            {create.isPending ? 'Нээж байна…' : 'Нээх'}
          </Button>
        </>
      }
    >
      {stores.isPending ? <Spinner /> : (
        <>
          <Field label="Салбар">
            <Select
              value={storeId}
              onChange={setStoreId}
              placeholder="Сонгох…"
              options={(stores.data?.data ?? []).map((s) => ({ value: s.id, label: `${s.code} · ${s.name}` }))}
            />
          </Field>
          <Field label="Тэмдэглэл"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
          {create.error ? <ErrorNote>{(create.error as Error).message}</ErrorNote> : null}
        </>
      )}
    </Modal>
  );
}

// ------------------------------------------------------------ Дэлгэрэнгүй
function CountDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const { can, readOnly } = useAuth();
  const qc = useQueryClient();
  const [raw, setRaw] = useState('');
  const [note, setNote] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [view, setView] = useState<'missing' | 'extra'>('missing');
  const [deleting, setDeleting] = useState(false);

  const detail = useQuery({
    queryKey: ['stock-count', id],
    queryFn: () => api<Envelope<CountRow>>(`/stock-counts/${id}`),
  });
  const progress = useQuery({
    queryKey: ['stock-count', id, 'progress'],
    queryFn: () => api<Envelope<Progress>>(`/stock-counts/${id}/progress`),
  });
  const missing = useQuery({
    queryKey: ['stock-count', id, 'missing'],
    queryFn: () => api<{ data: MissingRow[]; meta: { count: number; lossMnt: string } }>(`/stock-counts/${id}/missing`),
  });
  const extra = useQuery({
    queryKey: ['stock-count', id, 'extra'],
    queryFn: () => api<{ data: ExtraRow[]; meta: { count: number } }>(`/stock-counts/${id}/extra`),
  });

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['stock-count', id] }),
      qc.invalidateQueries({ queryKey: ['stock-counts'] }),
    ]);
  };

  const scan = useMutation({
    mutationFn: (serial: string) =>
      api(`/stock-counts/${id}/scan`, { method: 'POST', body: { serial } }),
    onSuccess: async () => { setRaw(''); setErr(null); setNote('Уншуулав'); await refresh(); },
    onError: (e: Error) => setErr(e.message),
  });

  const close = useMutation({
    mutationFn: () => api(`/stock-counts/${id}/close`, { method: 'POST', body: {} }),
    onSuccess: async () => { setNote('Тооллого хаагдлаа'); setErr(null); await refresh(); },
    onError: (e: Error) => setErr(e.message),
  });

  function submitScan(e: FormEvent) {
    e.preventDefault();
    const serial = normalizeSerialInput(raw);
    if (!isValidSerial(serial)) { setErr('Серийн дугаар буруу байна'); return; }
    scan.mutate(serial);
  }

  const c = detail.data?.data;
  const p = progress.data?.data;
  const canManage = can('STORE_MANAGER', 'WAREHOUSE', 'ADMIN') && !readOnly;
  const canScan = can('STORE_MANAGER', 'SALESPERSON', 'WAREHOUSE', 'ADMIN') && !readOnly;
  const isOpen = c?.status === 'OPEN';

  return (
    <Modal
      wide
      title={c ? `Тооллого ${c.code}` : 'Тооллого'}
      subtitle={c ? `${c.store.name} · ${c.status === 'OPEN' ? 'Нээлттэй' : 'Хаасан'}` : undefined}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Хаах</Button>
          {isOpen && canManage ? (
            <Button onClick={() => close.mutate()} disabled={close.isPending}>
              {close.isPending ? 'Хааж байна…' : 'Тооллого хаах'}
            </Button>
          ) : null}
          {can('ADMIN') && !readOnly ? (
            <Button variant="danger" onClick={() => setDeleting(true)}>Тооллого устгах</Button>
          ) : null}
        </>
      }
    >
      {detail.isPending || progress.isPending ? <Spinner /> : (
        <>
          {isOpen && canScan ? (
            <form onSubmit={submitScan} className="flex gap-2">
              <Input
                value={raw}
                onChange={(e) => setRaw(e.target.value)}
                placeholder="Гутлыг уншуулах"
                inputMode="numeric"
                autoFocus
                className="num"
                aria-label="Серийн дугаар"
              />
              <Button type="submit" disabled={scan.isPending} className="shrink-0">Уншуулах</Button>
            </form>
          ) : null}

          {err ? <ErrorNote>{err}</ErrorNote> : null}
          {note && !err ? <OkNote>{note}</OkNote> : null}

          {p ? (
            <>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <Stat label="Байвал зохих" value={formatQty(p.expected)} />
                <Stat label="Сканнасан" value={formatQty(p.scanned)} tone="accent" />
                <Stat label="Дутуу" value={formatQty(p.missing)} tone={p.missing > 0 ? 'alert' : 'default'} />
                <Stat label="Илүү" value={formatQty(p.extra)} tone={p.extra > 0 ? 'alert' : 'default'} />
              </div>
              <Meter value={p.percent} size="lg" label={`Тооллогын явц ${p.percent}%`} />
              <p className="text-[11.5px] text-ink-3">
                Явц {p.percent}% · нийт {formatQty(p.scans)} скан
              </p>
            </>
          ) : null}

          <div className="flex items-center gap-2">
            <Tabs
              value={view}
              onChange={setView}
              tabs={[
                { key: 'missing', label: 'Дутуу', count: missing.data?.meta.count },
                { key: 'extra', label: 'Илүү / танихгүй', count: extra.data?.meta.count },
              ]}
            />
            {view === 'missing' && missing.data ? (
              <span className="num ml-auto text-[12.5px] text-alert-ink">
                Алдагдал: {formatMnt(missing.data.meta.lossMnt)}
              </span>
            ) : null}
          </div>

          <div className="table-pin max-h-[38dvh] overflow-y-auto rounded-card border border-hair">
            {view === 'missing' ? (
              missing.isPending ? <Spinner /> : (
                <table className="w-full border-collapse text-[13px]">
                  <thead className="sticky top-0 bg-well-2">
                    <tr><Th>Серийн дугаар</Th><Th>Загвар</Th><Th num>Размер</Th><Th num>Өртөг</Th></tr>
                  </thead>
                  <tbody>
                    {(missing.data?.data ?? []).map((m) => (
                      <tr key={m.id} className="transition hover:bg-row-hover">
                        <Td><span className="num">{m.serial ? formatSerialDisplay(m.serial) : '—'}</span></Td>
                        <Td muted>{m.model.name}</Td>
                        <Td num>{m.size}</Td>
                        <Td num>{formatMnt(m.landedCostMnt)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            ) : extra.isPending ? <Spinner /> : (
              <table className="w-full border-collapse text-[13px]">
                <thead className="sticky top-0 bg-well-2">
                  <tr><Th>Уншуулсан код</Th><Th>Тайлбар</Th><Th num>Хугацаа</Th></tr>
                </thead>
                <tbody>
                  {(extra.data?.data ?? []).map((x) => (
                    <tr key={`${x.rawSerial}-${x.scannedAt}`} className="transition hover:bg-row-hover">
                      <Td><span className="num">{x.rawSerial}</span></Td>
                      <Td muted>
                        {x.unknown
                          ? 'Системд бүртгэлгүй код'
                          : `${x.item?.model.name ?? ''} · энэ салбарт байх ёсгүй`}
                      </Td>
                      <Td num muted>{formatDateTime(x.scannedAt)}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {(view === 'missing' ? missing.data?.data.length : extra.data?.data.length) === 0 ? (
              <Empty>{view === 'missing' ? 'Дутуу гутал алга' : 'Илүү скан алга'}</Empty>
            ) : null}
          </div>
        </>
      )}

      {deleting && c ? (
        <DeleteCount
          id={id}
          code={c.code}
          onClose={() => setDeleting(false)}
          onDone={async () => { await qc.invalidateQueries({ queryKey: ['stock-counts'] }); onClose(); }}
        />
      ) : null}
    </Modal>
  );
}

// ------------------------------------------------------------ Устгах
/** Тооллогыг устгана — гутал, үлдэгдэл хөндөгдөхгүй, зөвхөн скан алга болно. */
function DeleteCount({ id, code, onClose, onDone }: {
  id: string; code: string; onClose: () => void; onDone: () => void;
}) {
  const run = useMutation({
    mutationFn: () => api(`/stock-counts/${id}`, { method: 'DELETE' }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title={`${code} устгах уу?`}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Болих</Button>
          <Button variant="danger" onClick={() => run.mutate()} disabled={run.isPending}>
            {run.isPending ? 'Устгаж байна…' : 'Устгах'}
          </Button>
        </>
      }
    >
      <p className="text-[13px] text-ink-2">
        Тооллого болон уншуулсан бүх скан устгагдана. Гутал, үлдэгдэл, байршил өөрчлөгдөхгүй.
        Дахин тоолох бол шинэ тооллого нээнэ.
      </p>
      {run.error ? <ErrorNote>{(run.error as Error).message}</ErrorNote> : null}
    </Modal>
  );
}

function Stat({ label, value, tone = 'default' }: {
  label: string; value: string; tone?: 'default' | 'accent' | 'alert';
}) {
  const c = tone === 'alert' ? 'text-alert-ink' : tone === 'accent' ? 'text-accent-700' : 'text-ink';
  return (
    <div className="rounded-card border border-hair bg-well px-3 py-2">
      <div className="text-[11px] text-ink-2">{label}</div>
      <div className={`num text-[19px] leading-tight font-semibold ${c}`}>{value}</div>
    </div>
  );
}

function Th({ children, num = false }: { children: React.ReactNode; num?: boolean }) {
  return (
    <th
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair px-3 py-2.5 text-[11.5px] font-medium tracking-wide
        text-ink-2 uppercase whitespace-nowrap ${num ? 'text-right' : 'text-left'}`}
    >
      {children}
    </th>
  );
}

function Td({ children, num = false, muted = false }: {
  children: React.ReactNode; num?: boolean; muted?: boolean;
}) {
  return (
    <td
      {...(num ? { 'data-num': 'true' } : {})}
      className={`border-b border-hair/70 px-3 py-2.5 whitespace-nowrap
        ${num ? 'num text-right' : ''} ${muted ? 'text-ink-2' : ''}`}
    >
      {children}
    </td>
  );
}

/** Тооллогын жагсаалт — 640px-ээс доош тооллого тус бүрийн карт. */
function countColumns(): Column<CountRow>[] {
  return [
    { key: 'code', header: 'Дугаар', mobile: 'title', cell: (c) => <span className="num">{c.code}</span> },
    // Салбарын кирилл нэр — боогдоно
    { key: 'store', header: 'Салбар', cell: (c) => <span className="text-ink-2 text-pretty">{c.store.name}</span> },
    { key: 'expected', header: 'Байвал зохих', align: 'right', cell: (c) => formatQty(c.expectedCount) },
    { key: 'scanned', header: 'Сканнасан', align: 'right', mobile: 'value', cell: (c) => formatQty(c.scannedCount) },
    {
      key: 'missing',
      header: 'Дутуу',
      align: 'right',
      cell: (c) =>
        c.missingCount > 0 ? (
          <span className="text-alert-ink">{formatQty(c.missingCount)}</span>
        ) : (
          formatQty(c.missingCount)
        ),
    },
    { key: 'extra', header: 'Илүү', align: 'right', cell: (c) => formatQty(c.extraCount) },
    {
      key: 'status',
      header: 'Төлөв',
      cell: (c) => (
        <span
          className={`inline-block rounded-full px-2.5 py-[3px] text-[11px] whitespace-nowrap ${
            c.status === 'OPEN' ? 'bg-warn-bg text-warn-ink' : 'bg-accent-100 text-accent-700'
          }`}
        >
          {c.status === 'OPEN' ? 'Нээлттэй' : 'Хаасан'}
        </span>
      ),
    },
    {
      key: 'at',
      header: 'Огноо',
      align: 'right',
      cell: (c) => <span className="text-ink-2">{formatDateTime(c.closedAt ?? c.startedAt)}</span>,
    },
  ];
}
